import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LandingPage } from './components/LandingPage';
import { DeleteAccountPage } from './components/DeleteAccountPage';
import { SignIn } from './components/SignIn';
import { PackageSelection } from './components/PackageSelection';
import { Dashboard } from './components/Dashboard';
import { CalorieTracker } from './components/CalorieTracker';
import { AIAssistant } from './components/AIAssistant';
import { CommunityChallenges } from './components/CommunityChallenges';
import { TeleHealthConsult } from './components/TeleHealthConsult';
import { DailyStepsTracker } from './components/DailyStepsTracker';
import { GlucoseTracker } from './components/GlucoseTracker';
import { MedicationTracker } from './components/MedicationTracker';
import { InsulinTracker } from './components/InsulinTracker';
import { WeightTracker } from './components/WeightTracker';
import { BloodPressureTracker } from './components/BloodPressureTracker';
import { QuickAddMenu } from './components/QuickAddMenu';
import { Profile } from './components/Profile';
import { HealthScreen } from './components/HealthScreen';
import { HistoricalData } from './components/HistoricalData';
import { AppTopNav } from './components/AppTopNav';
import { HealthQuestionnaire } from './components/HealthQuestionnaire';
import { PackageManagement } from './components/PackageManagement';
import { VideosScreen } from './components/VideosScreen';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { subscriptionsService } from './services/subscriptions.service';
import { healthQuestionnaireService } from './services/health-questionnaire.service';

type AppPage =
  | 'dashboard'
  | 'health'
  | 'calorie-tracker'
  | 'ai-assistant'
  | 'packages'
  | 'manage-package'
  | 'community-challenges'
  | 'telehealth'
  | 'steps-tracker'
  | 'glucose-tracker'
  | 'medication-tracker'
  | 'insulin-tracker'
  | 'weight-tracker'
  | 'blood-pressure-tracker'
  | 'profile'
  | 'videos'
  | 'historical-data';

function AppContent() {
  const { user, loading: authLoading, signOut } = useAuth();
  const queryClient = useQueryClient();
  const [showSignIn, setShowSignIn] = useState(false);
  const [showPackagesPreview, setShowPackagesPreview] = useState(false);
  const [currentPage, setCurrentPage] = useState<AppPage>('dashboard');
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [questionnaireDeferred, setQuestionnaireDeferred] = useState(false);
  const [questionnairePromptDismissed, setQuestionnairePromptDismissed] = useState(false);
  const [questionnairePromptChoseComplete, setQuestionnairePromptChoseComplete] = useState(false);
  const [forceShowDashboard, setForceShowDashboard] = useState(false);

  // Check subscription status - only treat explicit "no subscription" as null, not API failures
  const subscriptionResult = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: () => subscriptionsService.getCurrentSubscription(),
    enabled: !!user,
    retry: 2,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 min to detect expiry during session
  });
  const subscription = subscriptionResult?.data ?? null;
  const isExpired = !!(subscription && (subscription as { isExpired?: boolean }).isExpired);
  const hasActiveSubscription = !!(subscription && !isExpired);
  const checkingSubscription = subscriptionResult?.isLoading ?? false;
  const subscriptionError = subscriptionResult?.error ?? null;
  const hasSubscriptionError = !!subscriptionError;
  const refetchSubscription = subscriptionResult?.refetch;

  // Clear force-show flag once we have real subscription data
  useEffect(() => {
    if (hasActiveSubscription && forceShowDashboard) setForceShowDashboard(false);
  }, [hasActiveSubscription, forceShowDashboard]);

  const questionnaireResult = useQuery({
    queryKey: ['questionnaire', user?.id],
    queryFn: async () => {
      try {
        return await healthQuestionnaireService.isCompleted();
      } catch (e) {
        console.warn('Questionnaire check failed:', e);
        return false;
      }
    },
    enabled: !!user,
    retry: false,
  });
  const questionnaireCompleted = questionnaireResult?.data ?? false;
  const checkingQuestionnaire = questionnaireResult?.isLoading ?? false;

  // Show confirmation prompt when: no subscription, questionnaire not done, user hasn't dismissed
  const showQuestionnairePrompt =
    !!user &&
    !subscription &&
    !questionnaireCompleted &&
    !questionnaireDeferred &&
    !questionnairePromptDismissed;

  // Show questionnaire when: not completed, not deferred, and user chose Complete from prompt
  const showQuestionnaire =
    !!user &&
    !questionnaireCompleted &&
    !questionnaireDeferred &&
    questionnairePromptChoseComplete;


  const handlePackageSelected = (opts?: { forceDashboard?: boolean }) => {
    if (opts?.forceDashboard) setForceShowDashboard(true);
    setCurrentPage('dashboard');
  };

  const navigateToPage = (page: string) => setCurrentPage(page as AppPage);

  const handleSignInClick = () => {
    setShowSignIn(true);
  };

  const loading =
    authLoading ||
    (user && checkingSubscription) ||
    (user && !questionnaireDeferred && checkingQuestionnaire);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-dropsugar-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && !showSignIn && !showPackagesPreview) {
    return (
      <LandingPage
        onSignIn={handleSignInClick}
        onViewPackages={() => setShowPackagesPreview(true)}
      />
    );
  }

  if (!user && showPackagesPreview) {
    return (
      <PackageSelection
        isGuestPreview
        onSignInRequired={handleSignInClick}
        onBack={() => setShowPackagesPreview(false)}
      />
    );
  }

  if (!user && showSignIn) {
    return <SignIn />;
  }

  // Block admin/doctor users - webapp is for regular users only
  const role = (user?.role ?? '').toString().toUpperCase();
  const isAdminOrDoctor = role === 'ADMIN' || role === 'SUPER_ADMIN' || role === 'DOCTOR';
  if (user && isAdminOrDoctor) {
    const adminUrl = '/admin.html';
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Admin Access Required</h2>
          <p className="text-gray-600 mb-6">
            You are logged in as an administrator. This app is for patients only. Please use the Admin Portal to manage the platform.
          </p>
          <div className="space-y-3">
            <a
              href={adminUrl}
              className="block w-full px-6 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white rounded-xl font-medium transition-all"
            >
              Go to Admin Portal
            </a>
            <button
              onClick={() => signOut()}
              className="block w-full px-6 py-3 border-2 border-gray-300 hover:border-gray-400 text-gray-700 rounded-xl font-medium transition-all"
            >
              Sign out and use a different account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Subscription check failed - don't assume "no subscription", show error with retry
  if (hasSubscriptionError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to load subscription</h2>
          <p className="text-gray-600 mb-6">
            We could not verify your subscription status. Please check your connection and try again.
          </p>
          <button
            onClick={() => refetchSubscription?.()}
            className="w-full px-6 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white rounded-xl font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Confirmation prompt before health assessment (like nz-dropsugar)
  if (showQuestionnairePrompt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to DropSugar!</h2>
          <p className="text-gray-700 mb-6">
            Before choosing your package, we recommend completing a quick health assessment.
            This helps us suggest the best package for your needs.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setQuestionnairePromptDismissed(true);
                setQuestionnairePromptChoseComplete(true);
              }}
              className="w-full px-6 py-3 bg-gradient-to-r from-dropsugar-green to-dropsugar-green-dark hover:opacity-90 text-white rounded-xl font-medium transition-all"
            >
              Complete Health Assessment
            </button>
            <button
              onClick={() => {
                setQuestionnairePromptDismissed(true);
                setQuestionnaireDeferred(true);
              }}
              className="w-full px-6 py-3 border-2 border-gray-300 hover:border-gray-400 text-gray-700 rounded-xl font-medium transition-all"
            >
              Skip and Choose Package
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show health questionnaire when user chose to complete it
  if (showQuestionnaire) {
    return (
      <HealthQuestionnaire
        onComplete={() => queryClient.invalidateQueries({ queryKey: ['questionnaire'] })}
        onSkip={() => setQuestionnaireDeferred(true)}
      />
    );
  }

  // Show package selection when: no subscription, expired (on login or during session), or user navigated to packages
  if ((!subscription || isExpired || currentPage === 'packages') && !forceShowDashboard) {
    return (
      <PackageSelection
        currentSubscription={subscription ?? undefined}
        isExpired={isExpired}
        onPackageSelected={handlePackageSelected}
        onBack={!isExpired && currentPage === 'packages' ? () => setCurrentPage('dashboard') : undefined}
      />
    );
  }

  const withAppLayout = (content: React.ReactNode) => (
    <>
      <AppTopNav
        currentPage={currentPage}
        onNavigate={navigateToPage}
        userEmail={user?.email}
        onSignOut={signOut}
      />
      {subscription && !isExpired && (() => {
        const exp = (subscription as any)?.trialExpiresAt || (subscription as any)?.expiresAt;
        if (!exp) return null;
        const expiry = new Date(exp);
        const now = new Date();
        const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        if (daysLeft <= 0 || daysLeft > 7) return null;
        return (
          <div key="expiry-banner" className="bg-amber-50 border-b-2 border-amber-300 px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-amber-900 font-medium">
                Your subscription {daysLeft === 1 ? 'expires tomorrow' : `expires in ${daysLeft} days`}. Renew now to avoid interruption.
              </p>
            </div>
            <button
              onClick={() => setCurrentPage('packages')}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors"
            >
              Renew
            </button>
          </div>
        );
      })()}
      {content}
    </>
  );

  if (currentPage === 'calorie-tracker') {
    return withAppLayout(
      <CalorieTracker
        onBack={() => setCurrentPage('dashboard')}
        onUpgrade={() => setCurrentPage('packages')}
      />
    );
  }

  if (currentPage === 'ai-assistant') {
    return withAppLayout(<AIAssistant onBack={() => setCurrentPage('dashboard')} />);
  }

  if (currentPage === 'community-challenges') {
    return withAppLayout(<CommunityChallenges onBack={() => setCurrentPage('dashboard')} />);
  }

  if (currentPage === 'telehealth') {
    return withAppLayout(<TeleHealthConsult onBack={() => setCurrentPage('dashboard')} />);
  }

  if (currentPage === 'steps-tracker') {
    return withAppLayout(
      <DailyStepsTracker
        onBack={() => setCurrentPage('dashboard')}
        onNavigate={navigateToPage}
      />
    );
  }

  if (currentPage === 'glucose-tracker') {
    return withAppLayout(<GlucoseTracker onBack={() => setCurrentPage('dashboard')} />);
  }

  if (currentPage === 'medication-tracker') {
    return withAppLayout(<MedicationTracker onBack={() => setCurrentPage('dashboard')} />);
  }

  if (currentPage === 'insulin-tracker') {
    return withAppLayout(<InsulinTracker onBack={() => setCurrentPage('dashboard')} />);
  }

  if (currentPage === 'weight-tracker') {
    return withAppLayout(<WeightTracker onBack={() => setCurrentPage('dashboard')} />);
  }

  if (currentPage === 'blood-pressure-tracker') {
    return withAppLayout(<BloodPressureTracker onBack={() => setCurrentPage('dashboard')} />);
  }

  if (currentPage === 'profile') {
    return withAppLayout(<Profile onBack={() => setCurrentPage('dashboard')} />);
  }

  if (currentPage === 'videos') {
    return withAppLayout(<VideosScreen />);
  }

  if (currentPage === 'manage-package') {
    return withAppLayout(
      <PackageManagement onBack={() => setCurrentPage('dashboard')} />
    );
  }

  if (currentPage === 'health') {
    return withAppLayout(<HealthScreen onNavigate={navigateToPage} />);
  }

  if (currentPage === 'historical-data') {
    return withAppLayout(<HistoricalData onBack={() => setCurrentPage('dashboard')} />);
  }

  const handleQuickAddSelect = (type: string) => {
    setShowQuickAdd(false);
    const pageMap: Record<string, string> = {
      'glucose': 'glucose-tracker',
      'medication': 'medication-tracker',
      'insulin': 'insulin-tracker',
      'weight': 'weight-tracker',
      'blood-pressure': 'blood-pressure-tracker',
      'calories': 'calorie-tracker',
      'steps': 'steps-tracker',
      'historical': 'historical-data',
    };
    const page = pageMap[type];
    if (page) {
      setCurrentPage(page as any);
    }
  };

  // Dashboard has its own nav (logo, Manage Package, Settings, Sign Out) - no AppTopNav
  return (
    <>
      <Dashboard
        onNavigate={(page) => setCurrentPage(page as any)}
        onQuickAdd={() => setShowQuickAdd(true)}
      />
      {showQuickAdd && (
        <QuickAddMenu
          onSelect={handleQuickAddSelect}
          onClose={() => setShowQuickAdd(false)}
        />
      )}
    </>
  );
}

function App() {
  const isDeleteAccountPath =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/delete-account' ||
      window.location.pathname === '/delete-account/');

  if (isDeleteAccountPath) {
    return <DeleteAccountPage />;
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
